import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatDistanceToNow } from "date-fns";
import { IMessage, TImageFormat, TDocumentFormat } from "./types";

export const GRAPHQL_API_URL = process.env.NEXT_PUBLIC_GRAPHQL_API_URL || "http://localhost:4000/graphql";
export const CHAT_API_URL = process.env.NEXT_PUBLIC_CHAT_API_URL || "http://localhost:4000/chat";
export const TITLE_WORD_LIMIT = parseInt(process.env.NEXT_PUBLIC_TITLE_WORD_LIMIT || "5", 10);

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function getImageFormat(file: File): TImageFormat | null {
    const mimeType = file.type.toLowerCase();
    if (mimeType === "image/png") return "png";
    if (mimeType === "image/jpeg" || mimeType === "image/jpg") return "jpeg";
    if (mimeType === "image/gif") return "gif";
    if (mimeType === "image/webp") return "webp";
    return null;
}

export function getDocumentFormat(file: File): TDocumentFormat | null {
    const fileName = file.name.toLowerCase();
    const extension = fileName.split(".").pop();

    if (extension === "pdf") return "pdf";
    if (extension === "csv") return "csv";
    if (extension === "doc") return "doc";
    if (extension === "docx") return "docx";
    if (extension === "xls") return "xls";
    if (extension === "xlsx") return "xlsx";
    if (extension === "html") return "html";
    if (extension === "txt") return "txt";
    if (extension === "md") return "md";
    return null;
}

export function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = (reader.result as string).split(",")[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export function extractDomain(url: string): string {
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname;
        const parts = hostname.split(".");

        if (parts.length >= 2) {
            return parts.slice(-2).join(".");
        }

        return hostname;
    } catch {
        return "";
    }
}

export function getFirstAssistantTextContent(messages: IMessage[]): string | undefined {
    const firstAssistantMessage = messages.find(
        (msg) => msg.role === "assistant" && msg.content.some((block) => block.text)
    );
    const textContent = firstAssistantMessage?.content.find((block) => block.text);
    return textContent?.text;
}

export function formatDate(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    // For very recent (< 1 minute), show "Just now"
    if (diffMs < 60000) {
        return "Just now";
    }

    // For all other times, show relative time (minutes, hours, days, months, years ago)
    const relativeTime = formatDistanceToNow(date, { addSuffix: true });
    // Capitalize first letter
    return relativeTime.charAt(0).toUpperCase() + relativeTime.slice(1);
}
